/**
 * Generic Cache Manager Utility
 *
 * Production-ready caching with comprehensive error handling and safety features:
 * - TTL (Time To Live) expiration with configurable durations
 * - LRU (Least Recently Used) eviction to prevent memory leaks
 * - Race condition prevention (fetching flag for concurrent requests)
 * - Type safety with TypeScript generics
 * - Automatic cleanup with configurable thresholds
 * - SSR-safe (client-side only operations)
 * - Memory leak prevention with max size limits
 * - Graceful error handling (never throws, always returns null)
 *
 * Used across product modal for:
 * - User reviews (30 min TTL, max 50 entries)
 * - Product variants/budflow_flavors (10 min TTL, max 30 entries)
 * - FAQs per store (30 min TTL, max 50 entries)
 *
 * Related files:
 * - ~/components/product-modal/product-variants.tsx
 * - ~/components/global-product-modal.tsx
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  lastAccessed: number;
  fetching?: boolean;
}

interface CacheConfig {
  ttl: number; // Time to live in milliseconds
  maxSize: number; // Maximum number of entries before eviction
  evictionPercent?: number; // Percentage to remove when full (default: 0.25)
  debug?: boolean; // Enable debug logging (default: false, auto-enabled in dev)
}

/**
 * Generic cache manager class
 * Handles all caching logic in one place to keep code DRY
 *
 * Example usage:
 * ```typescript
 * const reviewCache = createCache<Review | null>(CACHE_PRESETS.LONG);
 *
 * // Get cached data
 * const cached = reviewCache.get('product-123');
 * if (cached !== null) return cached;
 *
 * // Prevent duplicate requests
 * if (reviewCache.isFetching('product-123')) return;
 * reviewCache.markFetching('product-123');
 *
 * // Fetch and cache
 * const data = await fetchData();
 * reviewCache.set('product-123', data);
 * ```
 */
export class CacheManager<T> {
  private cache: Record<string, CacheEntry<T>> = {};
  private config: Required<CacheConfig>;
  private name: string; // For debugging

  constructor(config: CacheConfig, name: string = 'CacheManager') {
    this.config = {
      ...config,
      evictionPercent: config.evictionPercent ?? 0.25,
      debug:
        config.debug ?? (typeof process !== 'undefined' && process.env.NODE_ENV === 'development'),
    };
    this.name = name;

    // Validate config
    if (this.config.ttl <= 0) {
      console.warn(`[${this.name}] Invalid TTL: ${this.config.ttl}. Using default 10 minutes.`);
      this.config.ttl = 10 * 60 * 1000;
    }
    if (this.config.maxSize <= 0) {
      console.warn(`[${this.name}] Invalid maxSize: ${this.config.maxSize}. Using default 50.`);
      this.config.maxSize = 50;
    }
    if (this.config.evictionPercent <= 0 || this.config.evictionPercent >= 1) {
      console.warn(
        `[${this.name}] Invalid evictionPercent: ${this.config.evictionPercent}. Using default 0.25.`,
      );
      this.config.evictionPercent = 0.25;
    }

    if (this.config.debug) {
      this.log(`Initialized with TTL=${this.config.ttl}ms, maxSize=${this.config.maxSize}`);
    }
  }

  /**
   * Internal logging helper (only logs in debug mode)
   */
  private log(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[${this.name}] ${message}`, data ?? '');
    }
  }

  /**
   * Get cached data if available and not expired
   * Updates lastAccessed for LRU tracking
   *
   * @param key - Cache key to retrieve
   * @returns Cached data or null if not found/expired
   *
   * Error handling: Never throws - returns null on any error
   */
  get(key: string): T | null {
    try {
      // SSR safety check
      if (typeof window === 'undefined') {
        return null;
      }

      if (!key || typeof key !== 'string') {
        this.log(`Invalid key type: ${typeof key}`);
        return null;
      }

      const entry = this.cache[key];

      if (!entry) {
        this.log(`Cache miss: ${key}`);
        return null;
      }

      // Check expiration
      const now = Date.now();
      const age = now - entry.timestamp;
      const isExpired = age > this.config.ttl;

      if (isExpired) {
        this.log(`Cache expired: ${key} (age: ${Math.round(age / 1000)}s)`);
        delete this.cache[key];
        return null;
      }

      // Don't return data that's currently being fetched
      if (entry.fetching) {
        this.log(`Cache entry is being fetched: ${key}`);
        return null;
      }

      // Update last accessed for LRU
      entry.lastAccessed = now;
      this.log(`Cache hit: ${key} (age: ${Math.round(age / 1000)}s)`);
      return entry.data;
    } catch (error) {
      console.error(`[${this.name}] Error in get():`, error);
      return null;
    }
  }

  /**
   * Set cache data
   * Automatically cleans up old entries if cache is full
   *
   * @param key - Cache key to set
   * @param data - Data to cache
   *
   * Error handling: Never throws - silently fails on error
   */
  set(key: string, data: T): void {
    try {
      // SSR safety check
      if (typeof window === 'undefined') {
        return;
      }

      if (!key || typeof key !== 'string') {
        this.log(`Invalid key type: ${typeof key}`);
        return;
      }

      // Run cleanup before adding new entry
      this.cleanup();

      const now = Date.now();
      this.cache[key] = {
        data,
        timestamp: now,
        lastAccessed: now,
        fetching: false,
      };

      this.log(`Cache set: ${key}`, { size: Object.keys(this.cache).length });
    } catch (error) {
      console.error(`[${this.name}] Error in set():`, error);
    }
  }

  /**
   * Check if a key is currently being fetched (race condition prevention)
   *
   * @param key - Cache key to check
   * @returns true if currently fetching, false otherwise
   *
   * Error handling: Returns false on any error (safe default)
   */
  isFetching(key: string): boolean {
    try {
      // SSR safety check
      if (typeof window === 'undefined') {
        return false;
      }

      if (!key || typeof key !== 'string') {
        return false;
      }

      return this.cache[key]?.fetching === true;
    } catch (error) {
      console.error(`[${this.name}] Error in isFetching():`, error);
      return false;
    }
  }

  /**
   * Mark a key as fetching (prevents duplicate requests)
   * Should be called before starting an async fetch operation
   *
   * @param key - Cache key to mark as fetching
   *
   * Error handling: Never throws - silently fails on error
   */
  markFetching(key: string): void {
    try {
      // SSR safety check
      if (typeof window === 'undefined') {
        return;
      }

      if (!key || typeof key !== 'string') {
        this.log(`Invalid key type: ${typeof key}`);
        return;
      }

      // Run cleanup before adding new entry
      this.cleanup();

      const now = Date.now();
      this.cache[key] = {
        data: null as T, // Temporary null value during fetch
        timestamp: now,
        lastAccessed: now,
        fetching: true,
      };

      this.log(`Marked as fetching: ${key}`);
    } catch (error) {
      console.error(`[${this.name}] Error in markFetching():`, error);
    }
  }

  /**
   * Clear fetching flag (call if fetch fails)
   * Also useful for cleanup if fetch is abandoned
   *
   * @param key - Cache key to clear fetching flag
   *
   * Error handling: Never throws - silently fails on error
   */
  clearFetching(key: string): void {
    try {
      // SSR safety check
      if (typeof window === 'undefined') {
        return;
      }

      const entry = this.cache[key];
      if (entry) {
        entry.fetching = false;
        this.log(`Cleared fetching flag: ${key}`);
      }
    } catch (error) {
      console.error(`[${this.name}] Error in clearFetching():`, error);
    }
  }

  /**
   * Invalidate (remove) a specific cache entry
   * Useful for forcing a refetch or clearing stale data
   *
   * @param key - Cache key to invalidate
   *
   * Error handling: Never throws - silently fails on error
   */
  invalidate(key: string): void {
    try {
      // SSR safety check
      if (typeof window === 'undefined') {
        return;
      }

      if (!key || typeof key !== 'string') {
        return;
      }

      if (this.cache[key]) {
        delete this.cache[key];
        this.log(`Invalidated: ${key}`);
      }
    } catch (error) {
      console.error(`[${this.name}] Error in invalidate():`, error);
    }
  }

  /**
   * Clear all cache entries
   * Useful for logout scenarios or when store changes
   *
   * Error handling: Never throws - silently fails on error
   */
  clear(): void {
    try {
      // SSR safety check
      if (typeof window === 'undefined') {
        return;
      }

      const sizeBefore = Object.keys(this.cache).length;
      this.cache = {};
      this.log(`Cleared all entries (${sizeBefore} removed)`);
    } catch (error) {
      console.error(`[${this.name}] Error in clear():`, error);
    }
  }

  /**
   * Get cache statistics (for debugging and monitoring)
   *
   * @returns Object with cache metrics
   *
   * Error handling: Returns safe defaults on error
   */
  getStats() {
    try {
      const keys = Object.keys(this.cache);
      const now = Date.now();

      // Calculate age distribution
      const ages = keys.map((key) => {
        const entry = this.cache[key];
        return {
          key,
          age: Math.round((now - entry.timestamp) / 1000), // seconds
          fetching: entry.fetching || false,
        };
      });

      return {
        size: keys.length,
        maxSize: this.config.maxSize,
        ttl: this.config.ttl,
        ttlSeconds: Math.round(this.config.ttl / 1000),
        utilizationPercent: Math.round((keys.length / this.config.maxSize) * 100),
        entries: ages,
        oldestEntry: ages.length > 0 ? Math.max(...ages.map((a) => a.age)) : 0,
        fetchingCount: ages.filter((a) => a.fetching).length,
      };
    } catch (error) {
      console.error(`[${this.name}] Error in getStats():`, error);
      return {
        size: 0,
        maxSize: this.config.maxSize,
        ttl: this.config.ttl,
        ttlSeconds: 0,
        utilizationPercent: 0,
        entries: [],
        oldestEntry: 0,
        fetchingCount: 0,
      };
    }
  }

  /**
   * LRU cleanup - removes oldest accessed entries when cache is full
   * Also removes expired entries
   *
   * This method is automatically called by set() and markFetching()
   *
   * Cleanup strategy:
   * 1. Remove expired entries (age > TTL, not currently fetching)
   * 2. If still over max size, use LRU eviction
   * 3. Never remove entries that are currently fetching
   *
   * Error handling: Never throws - silently fails on error
   */
  private cleanup(): void {
    try {
      const cacheKeys = Object.keys(this.cache);

      // Early return if cache is empty or small
      if (cacheKeys.length === 0) {
        return;
      }

      const now = Date.now();
      let removedExpired = 0;

      // Phase 1: Remove expired entries (but keep fetching entries)
      for (const key of cacheKeys) {
        const entry = this.cache[key];

        // Skip if entry is currently being fetched (race condition protection)
        if (entry && !entry.fetching) {
          const isExpired = now - entry.timestamp > this.config.ttl;
          if (isExpired) {
            delete this.cache[key];
            removedExpired++;
          }
        }
      }

      if (removedExpired > 0) {
        this.log(`Cleaned up ${removedExpired} expired entries`);
      }

      // Phase 2: Check size after expired cleanup
      const remainingKeys = Object.keys(this.cache);

      if (remainingKeys.length >= this.config.maxSize) {
        // Sort by lastAccessed (oldest first) - LRU eviction
        const sortedKeys = remainingKeys
          .filter((key) => !this.cache[key]?.fetching) // Never evict fetching entries
          .sort((a, b) => this.cache[a].lastAccessed - this.cache[b].lastAccessed);

        // Calculate how many to remove (percentage of max size)
        const removeCount = Math.max(
          1, // Always remove at least 1
          Math.floor(this.config.maxSize * this.config.evictionPercent),
        );

        // Remove oldest entries
        let removedLRU = 0;
        for (let i = 0; i < removeCount && i < sortedKeys.length; i++) {
          const key = sortedKeys[i];
          if (this.cache[key] && !this.cache[key].fetching) {
            delete this.cache[key];
            removedLRU++;
          }
        }

        if (removedLRU > 0) {
          this.log(`LRU eviction: removed ${removedLRU} oldest entries`);
        }
      }

      // Log final size if in debug mode
      const finalSize = Object.keys(this.cache).length;
      if (this.config.debug && (removedExpired > 0 || finalSize >= this.config.maxSize * 0.8)) {
        this.log(
          `Cache size: ${finalSize}/${this.config.maxSize} (${Math.round((finalSize / this.config.maxSize) * 100)}%)`,
        );
      }
    } catch (error) {
      // Cleanup errors should never break the app
      console.error(`[${this.name}] Error in cleanup():`, error);
    }
  }
}

/**
 * Factory function to create cache instances with common configurations
 *
 * @param config - Cache configuration (TTL, max size, etc.)
 * @param name - Optional name for debugging (default: 'CacheManager')
 * @returns CacheManager instance
 *
 * Example:
 * ```typescript
 * const reviewCache = createCache<Review | null>(
 *   CACHE_PRESETS.LONG,
 *   'UserReviewCache'
 * );
 * ```
 */
export function createCache<T>(config: CacheConfig, name?: string): CacheManager<T> {
  return new CacheManager<T>(config, name);
}

/**
 * Predefined cache configurations for common use cases
 *
 * Usage guidelines:
 * - SHORT (5 min): Frequently changing data (deals, live inventory)
 * - MEDIUM (10 min): Semi-static data (product variants, suggested products)
 * - LONG (30 min): Rarely changing data (user reviews, brand logos, FAQs)
 * - EXTRA_LONG (1 hour): Very static data (site config, menu structure)
 */
export const CACHE_PRESETS = {
  // Short-lived cache for frequently changing data (5 min, 30 entries)
  SHORT: {
    ttl: 5 * 60 * 1000,
    maxSize: 30,
  },
  // Medium cache for semi-static data (10 min, 50 entries)
  MEDIUM: {
    ttl: 10 * 60 * 1000,
    maxSize: 50,
  },
  // Long cache for rarely changing data (30 min, 50 entries)
  LONG: {
    ttl: 30 * 60 * 1000,
    maxSize: 50,
  },
  // Extra long for very static data (1 hour, 100 entries)
  EXTRA_LONG: {
    ttl: 60 * 60 * 1000,
    maxSize: 100,
  },
} as const;

/**
 * Utility function to create a cache key from multiple parts
 * Useful for composite keys (e.g., "productId-storeCode")
 *
 * @param parts - Array of strings to join
 * @returns Cache key string
 *
 * Example:
 * ```typescript
 * const key = createCacheKey(['product', productId, selectedStore]);
 * // Returns: "product-123-embarcalameda"
 * ```
 */
export function createCacheKey(...parts: (string | number | undefined)[]): string {
  return parts
    .filter((part) => part !== undefined && part !== null && part !== '')
    .map((part) => String(part))
    .join('-');
}
